import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import {
  Folder,
  Save,
  HelpCircle,
  Settings,
  Home,
  Leaf,
  Undo,
  Redo,
  ChevronLeft,
} from 'lucide-react';
import logoIcon from '../../assets/icons/MeinGruenraum_Logo.png';
import GardenCanvas from './GardenCanvas';
import { GridProvider, useGrid } from '../../context/GridContext';
import { CommandProvider, useCommand } from '../../context/CommandContext';
import DynamicGardenObjectProperties from './PropertiesPanels/DynamicGardenObjectProperties';
import GateProperties from './PropertiesPanels/GateProperties';
import { usesOpeningSymbol } from './tools/gateSymbolRenderer';
import MultiSelectionProperties from './PropertiesPanels/MultiSelectionProperties';
import PlantPropertiesTab from './PropertiesPanels/PlantPropertiesTab';
import PlantFavoritesTab from './PropertiesPanels/PlantFavoritesTab';
import PlantCatalogDialog from './PropertiesPanels/PlantCatalogDialog';
import FirstTimeCatalogHint from './FirstTimeCatalogHint';
import useKeyboardShortcuts, { useTabShortcuts } from '../../hooks/useKeyboardShortcuts';
import SetupWizard from '../Wizard/SetupWizard';
import { useWizard } from '../../context/WizardContext';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import SettingsDialog from './SettingsDialog';
import EditorCoachTour from '../Onboarding/EditorCoachTour';
import { EDITOR_TOUR_STEPS, EDITOR_TOUR_DONE_KEY } from '../Onboarding/editorTourSteps';
import GardenObjectSidebar from './GardenObjectSidebar';
import ShapeToolbar from './ShapeToolbar';
import LayerPanel from './LayerPanel';
import { LayerProvider } from '../../context/LayerContext';
import { ShadowHeatmapProvider } from '../../context/ShadowHeatmapContext';
import { GardenTimeProvider } from '../../context/GardenTimeContext';
import { consumeBloomAutostart } from '../../constants/bloomAutostart';
import ViewPanelButtons from './ViewPanelButtons';
import FloatingViewPanel from './FloatingViewPanel';
import BloomPanel from './BloomPanel';
import SunShadowPanel from './SunShadowPanel';
import { getAllowedTools } from '../../config';
import useGardenPersistence, { mapFrontendToBackend, uploadBackgroundImageIfNeeded, serializeCanvasObjects } from '../../hooks/useGardenPersistence';
import ViewModeToggle from './Garden3D/ui/ViewModeToggle';
import { gardensApi } from '../../services/api';
import { isUserGroup } from './utils/groupUtils';
import openHelp from '../../utils/openHelp';
import PlantTabSidebar from './PlantTabSidebar';
import UserMenu from '../Auth/UserMenu';
import { UngroupObjectsCommand } from '../../commands/UngroupObjectsCommand';
import { usePlant } from '../../context/PlantContext';
import { useDashboard } from '../../context/DashboardContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useHasCoarsePointer } from '../../hooks/useHasCoarsePointer';
import MobileEditorBanner from './MobileEditorBanner';
import PropertiesDrawerSheet from './PropertiesDrawerSheet';
import SaveErrorToast from './SaveErrorToast';
import useAuth from '../../hooks/useAuth';

// 3D-Ansicht lazy geladen — three/R3F/drei landen in einem eigenen Chunk,
// das Haupt-Bundle bleibt unverändert (#599)
const Garden3DView = lazy(() => import('./Garden3D/Garden3DView'));

/** Suspense-Fallback, solange der 3D-Chunk lädt (#599) */
const Scene3DLoading = () => (
  <div className="flex h-full items-center justify-center bg-gray-50">
    <div className="text-center">
      <div
        className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"
        role="status"
        aria-label="3D-Ansicht wird geladen"
      />
      <p className="text-sm text-gray-500">3D-Ansicht wird geladen …</p>
    </div>
  </div>
);

/**
 * MainLayout - Main editor layout with three columns
 *
 * Layout Structure:
 * - Header (52px): Garden name, actions, user menu
 * - TabBar (48px): 2 tabs (Garten, Pflanzen)
 * - Body:
 *   - Toolbar (60px width, left): 8 vertical tool buttons
 *   - Canvas (flex-1, center): GardenCanvas component
 *   - PropertiesPanel (300px width, right): Properties display
 *
 * Wizard Integration:
 * - Shows SetupWizard on first launch (when isComplete = false)
 * - Wizard can be reopened from settings
 */
const MainLayout = () => {
  return (
    <CommandProvider maxHistory={50}>
      <GridProvider>
        <LayerProvider>
          <ShadowHeatmapProvider>
            <GardenTimeProvider>
              <MainLayoutContent />
            </GardenTimeProvider>
          </ShadowHeatmapProvider>
        </LayerProvider>
      </GridProvider>
    </CommandProvider>
  );
};

/**
 * MainLayoutContent - Inner component that uses GridContext
 */
const MainLayoutContent = () => {
  const { placementMode, exitPlacementMode, catalogReopenNonce, catalogReturnState } = usePlant();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('garten');
  const [activeTool, setActiveTool] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'select';
    return window.matchMedia('(pointer: coarse)').matches ? 'pan' : 'select';
  });
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedObjectType, setSelectedObjectType] = useState(null);
  const [fabricCanvas, setFabricCanvas] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 960, height: 600 });

  // View-Panels Blüte (#608) / Sonne & Schatten (#605): gegenseitig exklusiv;
  // Desktop dockt sie in die rechte Seitenleiste, Tablet/Mobile floating (#633).
  // Auto-Start aus der Dashboard-Karte „Jetzt blüht" (#609) via sessionStorage.
  const [activeViewPanel, setActiveViewPanel] = useState(() =>
    consumeBloomAutostart() ? 'bloom' : null
  );

  // 2D|3D-Ansicht (#599): Read-only-3D arbeitet auf einem Snapshot des
  // Canvas-Zustands; der Fabric-Canvas bleibt verborgen gemountet.
  const [viewMode, setViewMode] = useState('2d');
  const [sceneSnapshot, setSceneSnapshot] = useState(null);

  // Canvas ref for persistence hook
  const fabricCanvasRef = useRef(null);

  // Wizard detection: Check localStorage for completion flag
  const [showWizard, setShowWizard] = useState(() => {
    return !localStorage.getItem('gartenplaner_wizard_complete');
  });

  // Settings dialog state
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // Editor-Coach-Tour (#757): einmalige geführte Spotlight-Tour beim ersten
  // Editor-Besuch, jederzeit über die Einstellungen wieder aufrufbar.
  // Wiederkehrer (Wizard schon durch) bekommen sie direkt beim Mount; Neu-Nutzer
  // startet handleWizardComplete/-Skip nach der Einrichtung. Gerendert wird sie
  // ohnehin nur im editierbaren Layout (≥ 768px), daher hier keine isMobile-Gate.
  const [showTour, setShowTour] = useState(() => {
    try {
      const wizardDone = localStorage.getItem('gartenplaner_wizard_complete');
      return Boolean(wizardDone) && !localStorage.getItem(EDITOR_TOUR_DONE_KEY);
    } catch {
      return false;
    }
  });

  // Plant catalog dialog state
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  // Filters to pre-apply on the next catalog open — null for a plain open, a
  // flowering_months range when jumping in from a bloom gap (#662). Search/sort
  // are only seeded when reopening after an ESC-cancelled placement (#764).
  const [catalogInitialFilters, setCatalogInitialFilters] = useState(null);
  const [catalogInitialSearch, setCatalogInitialSearch] = useState(null);
  const [catalogInitialSort, setCatalogInitialSort] = useState(null);
  // Tracks the last handled reopen nonce so a cancel-driven reopen is applied
  // once during render (not in an effect) — see the render-phase block below (#764).
  const [prevCatalogReopenNonce, setPrevCatalogReopenNonce] = useState(catalogReopenNonce);

  // Get grid settings from context (now safe because we're inside GridProvider)
  const { gridSize, snapEnabled } = useGrid();

  // Get wizard state
  const { completeWizard, resetWizard, goToStep } = useWizard();

  // Get garden settings context
  const { setAllSettings, updateSettings, resetSettings, gardenId, setGardenId, setIsDirty, canvasObjects, canvasLoadNonce, setCanvasObjects } = useGardenSettings();

  // Persistence hook (startup load now lives in <GardenStartupLoader />, #484)
  const { saveToBackend, flushOnUnmount, saveStatus } = useGardenPersistence(fabricCanvasRef);

  // Get command stack for undo/redo
  const { commandStack } = useCommand();

  // Dashboard navigation + plant sync + mobile tab routing
  const { setActiveView, setPlacedPlants, setMobileTab } = useDashboard();

  // Breakpoint detection for responsive layout
  const { isMobile, isTablet } = useBreakpoint();
  const isDesktop = !isMobile && !isTablet;

  // Touch-device detection (independent of viewport width — iPad landscape ≥1024px still counts).
  // Used for default-tool selection so it stays in sync with the Pan-button visibility in sidebars.
  const isTouchDevice = useHasCoarsePointer();

  // Tour nach frisch abgeschlossener Einrichtung starten (Neu-Nutzer), sofern
  // noch nicht gesehen. Auf Mobile bleibt sie ungerendert (read-only Editor).
  const maybeStartTourAfterSetup = () => {
    try {
      if (!localStorage.getItem(EDITOR_TOUR_DONE_KEY)) setShowTour(true);
    } catch {
      setShowTour(true);
    }
  };

  const markTourDone = () => {
    try {
      localStorage.setItem(EDITOR_TOUR_DONE_KEY, 'true');
    } catch {
      // localStorage kann in Privat-Modi werfen — Tour trotzdem schließen.
    }
    setShowTour(false);
  };

  // Wiederaufruf aus den Einstellungen: in den passenden Kontext (Garten-Tab,
  // 2D) wechseln, damit die ersten Anker sichtbar sind, dann Tour starten.
  const handleStartTour = () => {
    setActiveTab('garten');
    setViewMode('2d');
    setShowTour(true);
  };

  // Calculate responsive canvas size (breakpoint-aware)
  useEffect(() => {
    const calculateCanvasSize = () => {
      const viewW = window.innerWidth;
      const viewH = window.innerHeight;
      const currentIsMobile = viewW < 768;
      const currentIsTablet = viewW >= 768 && viewW < 1024;

      if (currentIsMobile) {
        // Mobile: full-width canvas, height minus sub-header (44px) and BottomTabBar (68px)
        setCanvasSize({
          width: viewW,
          height: Math.max(300, viewH - 44 - 68),
        });
        return;
      }

      const LEFT_SIDEBAR_WIDTH = 280;
      const RIGHT_PANEL_WIDTH = currentIsTablet ? 0 : 300;
      const TOP_HEADER_HEIGHT = 52;
      const TOP_TABBAR_HEIGHT = 48;
      const VERTICAL_MARGINS = 32;

      const availableWidth = viewW - LEFT_SIDEBAR_WIDTH - RIGHT_PANEL_WIDTH;
      const availableHeight = viewH - TOP_HEADER_HEIGHT - TOP_TABBAR_HEIGHT - VERTICAL_MARGINS;

      setCanvasSize({
        width: Math.max(400, availableWidth),
        height: Math.max(400, availableHeight),
      });
    };

    calculateCanvasSize();
    window.addEventListener('resize', calculateCanvasSize);
    return () => window.removeEventListener('resize', calculateCanvasSize);
  }, []);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input field
      const isTyping = ['INPUT', 'TEXTAREA'].includes(e.target.tagName) ||
                       e.target.contentEditable === 'true';
      if (isTyping) return;

      // Ctrl+Z (or Cmd+Z on Mac) - Undo
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        commandStack.undo();
      }

      // Ctrl+Shift+Z (or Cmd+Shift+Z on Mac) - Redo
      // Also support Ctrl+Y (common Windows shortcut)
      if (
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') ||
        ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'y')
      ) {
        e.preventDefault();
        commandStack.redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [commandStack]);

  // Sync placed plants from canvas to DashboardContext whenever the canvas changes.
  // This keeps the Dashboard's plant count (and care-phase state) up-to-date.
  useEffect(() => {
    if (!fabricCanvas) return;

    const sync = () => {
      const plants = fabricCanvas.getObjects().filter(
        (obj) => obj.objectType === 'plant'
      );
      setPlacedPlants(plants);
    };

    // Initial sync after canvas becomes available
    sync();

    fabricCanvas.on('object:added', sync);
    fabricCanvas.on('object:removed', sync);

    return () => {
      fabricCanvas.off('object:added', sync);
      fabricCanvas.off('object:removed', sync);
    };
  }, [fabricCanvas, setPlacedPlants]);

  // 2D|3D umschalten (#599): beim Wechsel nach 3D einen Snapshot ziehen
  // (read-only, kein Live-Sync nötig). Die Auswahl bleibt bewusst bestehen
  // (#688) — der Fabric-Canvas bleibt verborgen gemountet, behält also sein
  // activeObject; würde hier nur der React-State geleert, käme man aus 3D mit
  // sichtbar selektiertem Objekt und leerem Eigenschaften-Panel zurück.
  const handleViewModeChange = (mode) => {
    if (mode === '3d') {
      setSceneSnapshot(serializeCanvasObjects(fabricCanvasRef.current));
    }
    setViewMode(mode);
  };

  // Reset to default tool when switching tabs (pan on touch devices, select on desktop)
  const handleTabChange = (newTab) => {
    // End placement mode on tab change — otherwise the instruction overlay stays
    // visible after the user navigates away from the Pflanzen tab (#429 R.5).
    if (placementMode) {
      exitPlacementMode();
    }
    // Tab-Wechsel führt zurück in die 2D-Bearbeitung (#599)
    setViewMode('2d');
    setActiveTab(newTab);
    setActiveTool(isTouchDevice ? 'pan' : 'select');
    // Auswahl auf beiden Seiten aufheben (#688): der Canvas bleibt über den
    // Tab-Wechsel gemountet, ein bloßes Leeren des React-States würde seine
    // Selektion samt Anfassern stehen lassen.
    fabricCanvasRef.current?.discardActiveObject();
    fabricCanvasRef.current?.renderAll();
    setSelectedObject(null);
    // Clear selected object type when leaving Garten tab
    // This prevents garden object metadata from being applied to shapes drawn in other tabs
    if (newTab !== 'garten') {
      setSelectedObjectType(null);
    }
  };

  // Tool definitions for keyboard shortcuts (must match sidebar tool sets)
  const toolsByTab = {
    garten: ['select', 'pan', 'path', 'lawn-edge', 'raised-bed', 'fence', 'gate', 'building', 'greenhouse', 'pond', 'terrace', 'compost', 'text-label'],
    pflanzen: ['select', 'pan', 'plant-catalog'],
  };

  // Get available tools for current tab
  const availableTools = toolsByTab[activeTab] || toolsByTab.garten;

  // Enable keyboard shortcuts for tab switching (1, 2, 3)
  useTabShortcuts(handleTabChange);

  // Enable keyboard shortcuts for tool selection (only for tools in current tab;
  // in der 3D-Ansicht deaktiviert — Werkzeuge wirken nur auf den 2D-Canvas, #599)
  useKeyboardShortcuts(setActiveTool, activeTool, activeTab, viewMode === '3d' ? [] : availableTools);

  // Handle canvas ready
  const handleCanvasReady = (canvas) => {
    setFabricCanvas(canvas);
    fabricCanvasRef.current = canvas;
  };

  // Startup garden load was hoisted to <GardenStartupLoader /> at the app root
  // (#484) so the Dashboard — the first view after login — gets the same
  // backend-authoritative garden state as the editor. GardenSettingsContext
  // .canvasObjects is hydrated there and read below as externalCanvasData;
  // GardenCanvas applies it on mount (see its init effect).

  // Handle selection change
  const handleSelectionChange = (selected) => {
    setSelectedObject(selected);
    // Auswahl gewinnt (#633): der Sidebar-Slot zeigt immer genau eines —
    // Objekt-Eigenschaften ODER View-Panel. Auf Tablet/Mobile (floating,
    // kein Slot-Konflikt) bleibt das Panel offen.
    if (selected && isDesktop) {
      setActiveViewPanel(null);
    }
  };

  // Handle property updates
  const handlePropertyUpdate = () => {
    if (fabricCanvas) {
      fabricCanvas.renderAll();
    }
  };

  // Handle object deletion
  const handleObjectDelete = (object) => {
    if (fabricCanvas && object) {
      fabricCanvas.remove(object);
      fabricCanvas.discardActiveObject();
      fabricCanvas.renderAll();
      setSelectedObject(null);

      // #482: Flush the deletion to the backend immediately (silent) so it is
      // server-side persisted within seconds — not stuck in the up-to-30s
      // auto-save window where a browser close / device switch would lose it.
      // Guarded by gardenId so we never auto-POST an unsaved garden (matches
      // the 30s-interval semantics); the in-flight guard (#496) coalesces this
      // with any concurrent save, so no PATCH storm.
      if (gardenId) {
        saveToBackend({ silent: true });
      }
    }
  };

  // Handle object deselection (for plant tab)
  const handleObjectDeselect = () => {
    if (fabricCanvas) {
      fabricCanvas.discardActiveObject();
      fabricCanvas.renderAll();
      setSelectedObject(null);
    }
  };

  // View-Panel öffnen/schließen (#633). Öffnen hebt die Objekt-Auswahl auf —
  // Gegenrichtung von „Auswahl gewinnt", der Slot zeigt immer genau eines.
  const toggleViewPanel = (panel) => {
    const next = activeViewPanel === panel ? null : panel;
    if (next) handleObjectDeselect();
    setActiveViewPanel(next);
  };

  // Katalog öffnen; optionale Vorfilter (#662). Jeder Öffnungspfad läuft hier
  // durch, damit ein Monatsfilter aus einer Blühlücke nicht in den nächsten
  // normalen Aufruf „durchsickert".
  const openCatalog = (filters = null) => {
    setCatalogInitialFilters(filters);
    // A fresh open never restores search/sort — those only come back on a
    // cancel-driven reopen (#764).
    setCatalogInitialSearch(null);
    setCatalogInitialSort(null);
    setShowCatalogDialog(true);
  };

  // Blühlücke → Katalog, vorgefiltert auf den Monat (#662). month ist 0-basiert;
  // flowering_months erwartet 1–12.
  const openCatalogForBloomMonth = (month) => {
    const oneBased = (((Math.trunc(Number(month)) % 12) + 12) % 12) + 1;
    openCatalog({ flowering_months: { start: oneBased, end: oneBased } });
  };

  // #764: reopen the catalog when a catalog-initiated placement was cancelled
  // (ESC), restoring the filters/search/sort the user had set. Handled as a
  // render-phase "adjust state on change" (the codebase avoids setState-in-effect).
  // The nonce only increments on a cancel, so this fires exactly once per cancel.
  if (catalogReopenNonce !== prevCatalogReopenNonce) {
    setPrevCatalogReopenNonce(catalogReopenNonce);
    setCatalogInitialFilters(catalogReturnState?.filters ?? null);
    setCatalogInitialSearch(catalogReturnState?.searchQuery ?? null);
    setCatalogInitialSort(catalogReturnState?.sortBy ?? null);
    setShowCatalogDialog(true);
  }

  // Panel-Inhalt des aktiven View-Panels (Desktop: Sidebar-Slot, sonst floating).
  // onFindPlantsForMonth nur dort durchreichen, wo der Katalog erreichbar ist
  // (Desktop/Tablet) — im read-only-Mobile-Editor bleibt der Hinweis aus.
  const renderActiveViewPanel = (onFindPlantsForMonth) =>
    activeViewPanel === 'bloom' ? (
      <BloomPanel
        canvas={fabricCanvas}
        onClose={() => setActiveViewPanel(null)}
        onFindPlantsForMonth={onFindPlantsForMonth}
      />
    ) : activeViewPanel === 'sun' ? (
      <SunShadowPanel canvas={fabricCanvas} onClose={() => setActiveViewPanel(null)} />
    ) : null;

  // Handle object type selection (Garten tab workflow)
  const handleObjectTypeSelection = (objectTypeId) => {
    // Update selected object type
    setSelectedObjectType(objectTypeId);

    // Get allowed tools for this object type
    const allowedTools = getAllowedTools(objectTypeId);

    // Activate the first allowed tool
    if (allowedTools && allowedTools.length > 0) {
      // Map from config tool names (DRAWING_TOOLS enum values) to UI tool names
      // Note: Config uses 'bezier', UI uses 'curve'
      const toolMapping = {
        'rectangle': 'rectangle',
        'circle': 'circle',
        'polygon': 'polygon',
        'bezier': 'curve',
        'line': 'line',
        'point': 'point',
        'centerline': 'centerline', // #720 Weg-Mittellinie
      };

      const firstAllowedTool = toolMapping[allowedTools[0]];
      if (firstAllowedTool) {
        setActiveTool(firstAllowedTool);
      }
    }
  };

  // Handle new garden (clear canvas)
  const handleNewGarden = () => {
    if (!fabricCanvas) return;

    // Check if there are any objects (excluding grid lines and snap guides)
    const objects = fabricCanvas.getObjects();
    const hasObjects = objects.some(obj => !obj.data?.isGridLine && !obj.data?.isSnapGuide);

    // Check specifically for plants
    const plants = objects.filter(obj => obj.objectType === 'plant');
    const hasPlants = plants.length > 0;

    if (hasObjects) {
      // Customize warning message if plants are present
      let message = 'Möchten Sie wirklich einen neuen Garten erstellen? Alle aktuellen Objekte werden gelöscht.';

      if (hasPlants) {
        message = `Möchten Sie wirklich einen neuen Garten erstellen? Alle aktuellen Objekte und alle ${plants.length} platzierten Pflanzen werden entfernt.`;
      }

      const confirmed = window.confirm(message);

      if (!confirmed) return;
    }

    // Clear localStorage (canvas objects)
    localStorage.removeItem('gartenplaner_canvas');

    // Reset gardenId so next save creates a new garden (POST)
    setGardenId(null);

    // Clear the backend-loaded snapshot in context (#621): it drives the
    // externalCanvasData effect in GardenCanvas — left stale, it re-applies
    // the OLD garden's objects onto the cleared canvas as soon as the effect
    // re-runs (callback identity change or canvas re-init), and auto-save
    // would then persist them into the NEW garden.
    setCanvasObjects(null);

    // Note: We keep favorites (gartenplaner_favorites) intact
    // Favorites are personal preferences that transcend specific gardens

    // Remove all objects except grid lines and snap guides
    const objectsToRemove = objects.filter(obj => !obj.data?.isGridLine && !obj.data?.isSnapGuide);
    objectsToRemove.forEach(obj => fabricCanvas.remove(obj));

    // Clear selection
    fabricCanvas.discardActiveObject();
    fabricCanvas.renderAll();
    setSelectedObject(null);

    console.log('New garden created');
  };

  // Build a centered rectangle polygon (4 vertices) from width/depth in meters.
  // Scales to fit the 960×600 wizard canvas with 40px padding on each side.
  const buildRectVertices = (widthM, depthM) => {
    const canvasW = 960, canvasH = 600, padding = 40;
    const scale = Math.min((canvasW - padding * 2) / widthM, (canvasH - padding * 2) / depthM);
    const pxW = widthM * scale;
    const pxH = depthM * scale;
    const x0 = (canvasW - pxW) / 2;
    const y0 = (canvasH - pxH) / 2;
    return [
      { x: x0,        y: y0 },
      { x: x0 + pxW,  y: y0 },
      { x: x0 + pxW,  y: y0 + pxH },
      { x: x0,        y: y0 + pxH },
    ];
  };

  // Handle wizard completion
  const handleWizardComplete = (wizardData) => {
    console.log('Wizard completed with data:', wizardData);

    // Extract calculated dimensions from outline
    const calculatedDims = wizardData.outline?.calculatedDimensions;

    // Mobile rect outline: convert slider dimensions to a centered rectangle polygon
    const mobileRect = wizardData.outline?.skipped && wizardData.outline?.rect;
    const outlineVertices = mobileRect
      ? buildRectVertices(wizardData.outline.rect.width, wizardData.outline.rect.depth)
      : wizardData.outline?.vertices ?? [];

    // Build the complete settings object once (used for both context + initial backend save)
    const newSettings = {
      // Step 1: Garden basics
      ...wizardData.basics,

      // Auto-calculated dimensions from Step 3 outline
      length: calculatedDims?.lengthMeters ?? wizardData.outline?.rect?.depth ?? 0,
      width:  calculatedDims?.widthMeters  ?? wizardData.outline?.rect?.width  ?? 0,
      area:   calculatedDims?.areaSquareMeters
        ?? (mobileRect ? wizardData.outline.rect.width * wizardData.outline.rect.depth : 0),

      // Step 2: Background image (optional)
      // Keep both dataUrl (for local canvas preview) and file (for upload on save)
      backgroundImage: wizardData.backgroundImage?.dataUrl
        ? {
            dataUrl: wizardData.backgroundImage.dataUrl,
            file: wizardData.backgroundImage.file || null,  // File object for upload
            transparency: wizardData.backgroundImage.transparency ?? 50,
            position: wizardData.backgroundImage.position || { x: 20, y: 20 },
            rotation: wizardData.backgroundImage.rotation || 0,
            calibration: wizardData.backgroundImage.calibration || {
              scale: 100,  // Default: 1px = 1cm
              isCalibrated: false,
              realDistance: 5,
              pixelDistance: null
            },
            locked: true,  // Always locked in GardenCanvas
            canvasSize: { width: 960, height: 600 }  // Wizard canvas dimensions
          }
        : null,

      // Step 3: Garden outline — real polygon or mobile rect placeholder
      outline: outlineVertices.length >= 3
        ? {
            vertices: outlineVertices,
            isClosed: true,
            canvasSize: { width: 960, height: 600 }
          }
        : null,

      // Step 4: Height differences (optional) — Zonen und/oder Grundgefälle (#624)
      heightDifferences: (wizardData.heightDifferences?.enabled || wizardData.heightDifferences?.baseSlope)
        ? {
            enabled: !!wizardData.heightDifferences?.enabled,
            zones: wizardData.heightDifferences?.enabled
              ? wizardData.heightDifferences.zones || []
              : [],
            ...(wizardData.heightDifferences?.baseSlope && {
              baseSlope: wizardData.heightDifferences.baseSlope,
            }),
            canvasSize: { width: 960, height: 600 }  // Wizard canvas dimensions
          }
        : null,
    };

    // 1. Save ALL wizard data to GardenSettingsContext
    setAllSettings(newSettings);

    // 2. Mark wizard as complete in WizardContext
    completeWizard();

    // 3. Set localStorage flag for wizard completion
    localStorage.setItem('gartenplaner_wizard_complete', 'true');

    // 4. Hide wizard and show editor
    setShowWizard(false);

    // 4b. Erste Editor-Einführung anbieten (#757), sofern noch nicht gesehen.
    maybeStartTourAfterSetup();

    // 5. Persist active view so a hard reload restores the editor instead of
    //    landing on Dashboard State 0 (#456). Without this, anon users see the
    //    "Garten einrichten" onboarding after F5 even though the garden exists.
    setActiveView('editor');

    // 6. Initial backend save for new gardens — cannot rely on saveToBackend here because
    // settingsRef.current is updated asynchronously after state propagates. Instead, use
    // newSettings directly. This ensures the garden is immediately findable in
    // GardenSwitcherSheet (which calls gardensApi.getAll on open).
    const existingGardenId = localStorage.getItem('gartenplaner_active_garden_id');
    if (!existingGardenId && newSettings.outline) {
      (async () => {
        try {
          // #424: upload background image first so the payload carries imageUrl.
          // Mirrors the auto-save path in useGardenPersistence.js.
          let settingsForBackend = newSettings;
          if (newSettings.backgroundImage && !newSettings.backgroundImage.imageUrl) {
            const uploadedBgImage = await uploadBackgroundImageIfNeeded(newSettings.backgroundImage);
            settingsForBackend = { ...newSettings, backgroundImage: uploadedBgImage };
            // Propagate imageUrl back into context so the next auto-save tick
            // doesn't re-upload the same file.
            updateSettings({ backgroundImage: uploadedBgImage });
          }
          const payload = mapFrontendToBackend(settingsForBackend, null, []);
          const created = await gardensApi.create(payload);
          setGardenId(String(created.id));
          // Anonymous gardens: remember the id so AuthContext.verifyToken can
          // claim it on the next magic-link login (#446 C-1). Authenticated
          // users already get gardens.user_id set server-side; skipping the
          // write here prevents the localStorage list from growing forever
          // across repeated anon→auth cycles.
          if (!isAuthenticated) {
            const existingIds = JSON.parse(localStorage.getItem('my_garden_ids') || '[]');
            const newId = String(created.id);
            if (!existingIds.includes(newId)) {
              localStorage.setItem('my_garden_ids', JSON.stringify([...existingIds, newId]));
            }
          }
        } catch (err) {
          console.error('Initial garden save failed:', err.message);
        }
      })();
    }
  };

  // Handle re-run wizard with mode parameter
  // Modes: 'edit' (keep data), 'reset' (clear wizard only), 'new' (clear everything)
  const handleRerunWizard = (mode = 'reset') => {
    if (mode === 'reset') {
      // Clear wizard data, keep canvas objects
      resetWizard();
      resetSettings();
    } else if (mode === 'new') {
      // Clear everything: wizard data + canvas objects
      resetWizard();
      resetSettings();

      // Clear canvas objects (but keep grid lines and snap guides)
      if (fabricCanvas) {
        const objects = fabricCanvas.getObjects();
        const objectsToRemove = objects.filter(
          obj => !obj.data?.isGridLine && !obj.data?.isSnapGuide
        );
        objectsToRemove.forEach(obj => fabricCanvas.remove(obj));
        fabricCanvas.discardActiveObject();
        fabricCanvas.renderAll();
      }

      // Clear all garden-related localStorage keys
      const keysToRemove = Object.keys(localStorage).filter(key =>
        key.startsWith('gartenplaner_')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Reset gardenId state so next save creates a new garden
      setGardenId(null);

      // Stalen Backend-Snapshot leeren (#621) — sonst spielt der
      // externalCanvasData-Effekt die Objekte des alten Gartens wieder ein
      setCanvasObjects(null);
    } else if (mode === 'edit') {
      // For edit mode: Keep data, but reset to step 1
      goToStep(1);
    }

    // Remove completion flag and show wizard
    localStorage.removeItem('gartenplaner_wizard_complete');
    setShowWizard(true);
  };

  // Settings Dialog Handlers

  // Handler for "Einstellungen bearbeiten" button
  const handleEditSettings = () => {
    handleRerunWizard('edit');
  };

  // Handler for "Setup-Assistenten zurücksetzen" button (from settings dialog)
  const handleResetWizardFromDialog = () => {
    handleRerunWizard('reset');
  };

  // Handler for "Neuen Garten erstellen" button (from settings dialog)
  const handleNewGardenFromDialog = () => {
    handleRerunWizard('new');
  };

  // Handle wizard skip
  const handleWizardSkip = (stepNumber) => {
    console.log(`Wizard step ${stepNumber} skipped`);
    // Step-specific skip logic can be added here if needed
  };

  // ─────────────────────────────────────────────────────────────
  // Mobile layout (< 768px): read-only canvas + floating banner
  // ─────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="flex flex-col bg-white font-inter" style={{ height: 'calc(100dvh - 68px)' }}>
        {/* Mobile Sub-Header (44px): back button + garden name + READ-ONLY badge + 2D|3D */}
        <MobileEditorSubHeader
          onBack={() => setMobileTab?.('garten')}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />

        {/* Failed-save notification (covers manual + silent auto-save, #495) */}
        <SaveErrorToast saveStatus={saveStatus} />

        {/* Canvas area — relative container for the floating card */}
        <div className="flex-1 overflow-hidden relative">
          {/* Fabric-Canvas bleibt in 3D verborgen gemountet (#599) */}
          <div className="h-full" style={{ display: viewMode === '3d' ? 'none' : undefined }}>
            <GardenCanvas
              width={canvasSize.width}
              height={canvasSize.height}
              onCanvasReady={handleCanvasReady}
              activeTool="select"
              onToolChange={() => {}}
              onSelectionChange={handleSelectionChange}
              onObjectDelete={handleObjectDelete}
              activeTab={activeTab}
              selectedObjectType={null}
              externalCanvasData={canvasObjects}
              externalCanvasNonce={canvasLoadNonce}
              onCanvasDirty={() => {}}
              readOnly
              viewMode={viewMode}
            />

            {/* Floating "Bearbeitung am Desktop" card */}
            <MobileEditorBanner />

            {/* View-Panels (#633): auf Mobile wie bisher floating — die
                View-Modi sind reine Ansichten und passen zum read-only-Editor */}
            {fabricCanvas && (
              <>
                <ViewPanelButtons activePanel={activeViewPanel} onToggle={toggleViewPanel} />
                {activeViewPanel && (
                  <FloatingViewPanel>{renderActiveViewPanel()}</FloatingViewPanel>
                )}
              </>
            )}
          </div>

          {viewMode === '3d' && (
            <div className="h-full relative overflow-hidden">
              <Suspense fallback={<Scene3DLoading />}>
                <Garden3DView canvasJson={sceneSnapshot} />
              </Suspense>
            </div>
          )}
        </div>

        {/* Properties bottom sheet — overlays BottomTabBar; in 3D ausgeblendet
            wie das Desktop-PropertiesPanel (read-only, keine Selektion — und der
            Sheet-Griff würde die Kamera-Preset-Buttons verdecken, #603) */}
        {viewMode === '2d' && (
          <PropertiesDrawerSheet
            selectedObject={selectedObject}
            onDelete={handleObjectDelete}
            onUpdate={handlePropertyUpdate}
            canvas={fabricCanvas}
          />
        )}

        {showWizard && (
          <SetupWizard onComplete={handleWizardComplete} onSkip={handleWizardSkip} />
        )}
        <SettingsDialog
          isOpen={showSettingsDialog}
          onClose={() => setShowSettingsDialog(false)}
          onEditSettings={handleEditSettings}
          onResetWizard={handleResetWizardFromDialog}
          onNewGarden={handleNewGardenFromDialog}
        />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Tablet + Desktop layout (≥ 768px)
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-white font-inter" style={{ minWidth: isDesktop ? 1024 : 'auto' }}>
      {/* Header - 52px */}
      <Header
        onNewGarden={handleNewGarden}
        onOpenSettings={() => setShowSettingsDialog(true)}
        onSave={() => saveToBackend()}
        saveStatus={saveStatus}
        onGoToDashboard={() => setActiveView('dashboard')}
      />

      {/* Failed-save notification (covers manual + silent auto-save, #495) */}
      <SaveErrorToast saveStatus={saveStatus} />

      {/* Tab Bar - 48px */}
      <TabBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        canvas={fabricCanvas}
        gridSize={gridSize}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {/* Main Content Area - remaining height */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - 280px in both tabs for consistent layout; in 3D ausgeblendet (#599) */}
        {viewMode === '2d' && (
          <div className="flex-shrink-0 overflow-hidden w-[280px]">
            {activeTab === 'garten' ? (
              <div data-tour-id="tour-objects" className="h-full">
                <GardenObjectSidebar
                  selectedObjectType={selectedObjectType}
                  onSelectObjectType={handleObjectTypeSelection}
                  activeTool={activeTool}
                  onToolChange={setActiveTool}
                />
              </div>
            ) : (
              <PlantTabSidebar
                activeTool={activeTool}
                onToolChange={setActiveTool}
                onOpenCatalog={() => openCatalog()}
              />
            )}
          </div>
        )}

        {/* Canvas - flexible center */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white relative">
          {/* Shape Toolbar - animates height on tab switch */}
          <div
            data-tour-id="tour-draw"
            className="overflow-hidden flex-shrink-0"
            style={{
              height: activeTab === 'garten' && viewMode === '2d' ? '60px' : '0px',
              transition: 'height 200ms ease-out',
            }}
          >
            <ShapeToolbar
              selectedObjectType={selectedObjectType}
              activeTool={activeTool}
              onToolChange={setActiveTool}
            />
          </div>

          {activeTab === 'pflanzen' && viewMode === '2d' && <FirstTimeCatalogHint />}

          {/* Fabric-Canvas bleibt in 3D verborgen gemountet — kein Re-Init,
              kein State-Verlust beim Zurückschalten (#599) */}
          <div
            className="flex-1 overflow-auto"
            style={{ display: viewMode === '3d' ? 'none' : undefined }}
          >
            <GardenCanvas
              width={canvasSize.width}
              height={canvasSize.height}
              onCanvasReady={handleCanvasReady}
              activeTool={activeTool}
              onToolChange={setActiveTool}
              onSelectionChange={handleSelectionChange}
              onObjectDelete={handleObjectDelete}
              activeTab={activeTab}
              selectedObjectType={selectedObjectType}
              externalCanvasData={canvasObjects}
              externalCanvasNonce={canvasLoadNonce}
              onCanvasDirty={() => setIsDirty(true)}
              onUnmountFlush={flushOnUnmount}
              viewMode={viewMode}
            />
          </div>

          {viewMode === '3d' && (
            <div className="flex-1 relative overflow-hidden">
              <Suspense fallback={<Scene3DLoading />}>
                <Garden3DView canvasJson={sceneSnapshot} />
              </Suspense>
            </div>
          )}
        </div>

        {/* Properties Panel — desktop only (≥ 1024px), in 3D ausgeblendet (#599) */}
        {isDesktop && viewMode === '2d' && (
          <PropertiesPanel
            activeTab={activeTab}
            activeTool={activeTool}
            selectedObject={selectedObject}
            onUpdate={handlePropertyUpdate}
            onDelete={handleObjectDelete}
            onDeselect={handleObjectDeselect}
            gridSize={gridSize}
            snapEnabled={snapEnabled}
            // Filter durchreichen (#700): der Beet-Einstieg „Pflanzen für dieses
            // Beet" öffnet den Katalog vorgefiltert. Bestehende Aufrufer ohne
            // Argument öffnen weiterhin ungefiltert (openCatalog default null).
            onOpenCatalog={openCatalog}
            canvas={fabricCanvas}
            viewPanel={renderActiveViewPanel(openCatalogForBloomMonth)}
          />
        )}
      </div>

      {/* View-Panels Blüte / Sonne & Schatten (#633): Button-Paar unten mittig;
          Desktop dockt den Inhalt in die Seitenleiste (viewPanel-Prop oben),
          Tablet (keine Seitenleiste) floated ihn über den Buttons */}
      {viewMode === '2d' && fabricCanvas && (
        <>
          {/* Ausblenden, während aktiv editiert wird (Zeichen-/Platzier-Werkzeug
              oder aktive Objekt-Auswahl), damit das Paar die Zeichenfläche unten
              mittig nicht blockiert (#650). 'pan'/'select' ohne Auswahl = neutrale
              Ansicht → sichtbar, sonst kein Einstieg in die View-Panels. */}
          <ViewPanelButtons
            activePanel={activeViewPanel}
            onToggle={toggleViewPanel}
            hidden={(activeTool !== 'select' && activeTool !== 'pan') || Boolean(selectedObject)}
          />
          {!isDesktop && activeViewPanel && (
            <FloatingViewPanel>{renderActiveViewPanel(openCatalogForBloomMonth)}</FloatingViewPanel>
          )}
        </>
      )}

      {/* Setup Wizard (conditional) */}
      {showWizard && (
        <SetupWizard onComplete={handleWizardComplete} onSkip={handleWizardSkip} />
      )}

      {/* Settings Dialog */}
      <SettingsDialog
        isOpen={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
        onEditSettings={handleEditSettings}
        onResetWizard={handleResetWizardFromDialog}
        onNewGarden={handleNewGardenFromDialog}
        onStartTour={handleStartTour}
      />

      {/* Editor-Coach-Tour (#757) — nur im editierbaren Layout (≥ 768px) */}
      {showTour && <EditorCoachTour steps={EDITOR_TOUR_STEPS} onClose={markTourDone} />}

      {/* Plant Catalog Dialog */}
      <PlantCatalogDialog
        isOpen={showCatalogDialog}
        onClose={() => setShowCatalogDialog(false)}
        initialFilters={catalogInitialFilters}
        initialSearchQuery={catalogInitialSearch}
        initialSortBy={catalogInitialSort}
        onPlantPlace={(plant) => {
          console.log('Plant placed:', plant);
          setShowCatalogDialog(false);
        }}
      />
    </div>
  );
};

/**
 * Header Component - 52px height
 * Contains: Garden name, action buttons, user controls
 */
const Header = ({ onNewGarden, onOpenSettings, onSave, saveStatus, onGoToDashboard }) => {
  const { settings } = useGardenSettings();
  const { commandStack } = useCommand();
  const handleUndo = () => {
    commandStack.undo();
  };

  const handleRedo = () => {
    commandStack.redo();
  };

  return (
    <header className="relative flex items-center justify-between h-[52px] px-4 bg-white border-b border-gray-200">
      {/* Left: Brand + Garden name */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <img src={logoIcon} alt="Mein Grünraum" className="w-9 h-9" />
          <span className="text-sm font-semibold tracking-tight">
            <span className="text-gray-900">Mein </span>
            <span className="text-emerald-600">Grünraum</span>
          </span>
        </div>
        <div className="w-px h-6 bg-gray-300" />
        <h1
          className="text-sm font-medium text-gray-600 truncate max-w-xs"
          title={settings?.name || 'Mein Garten'}
        >
          {settings?.name || 'Mein Garten'}
        </h1>
        <button
          onClick={onNewGarden}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          aria-label="Neuer Garten"
          title="Neuer Garten"
        >
          <Folder size={20} />
        </button>
        {/* Undo/Redo buttons */}
        <div className="flex items-center gap-1 ml-2 border-l border-gray-300 pl-2">
          <button
            onClick={handleUndo}
            disabled={!commandStack.canUndo()}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Rückgängig"
            title={`Rückgängig (Ctrl+Z)${commandStack.canUndo() ? ': ' + commandStack.getUndoDescription() : ''}`}
          >
            <Undo size={20} />
          </button>
          <button
            onClick={handleRedo}
            disabled={!commandStack.canRedo()}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Wiederholen"
            title={`Wiederholen (Ctrl+Shift+Z)${commandStack.canRedo() ? ': ' + commandStack.getRedoDescription() : ''}`}
          >
            <Redo size={20} />
          </button>
        </div>
      </div>

      {/* Center: Action buttons (absolute to stay truly centered) */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
        <button
          onClick={onSave}
          disabled={saveStatus === 'saving'}
          className={`flex items-center gap-2 px-4 py-2 border rounded transition-all font-medium text-sm ${
            saveStatus === 'saved'
              ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
              : saveStatus === 'error' || saveStatus === 'session-expired'
              ? 'border-red-300 text-red-700 bg-red-50'
              : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Save size={16} />
          {saveStatus === 'saving'
            ? 'Speichern...'
            : saveStatus === 'saved'
            ? 'Gespeichert'
            : saveStatus === 'session-expired'
            ? 'Sitzung abgelaufen'
            : saveStatus === 'error'
            ? 'Fehler – erneut speichern'
            : 'Speichern'}
        </button>
      </div>

      {/* Right: User controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onGoToDashboard}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          aria-label="Dashboard"
          title="Zurück zum Dashboard"
        >
          <Home size={20} />
        </button>
        <button
          onClick={() => openHelp('editor')}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          aria-label="Hilfe"
          title="Hilfe & Handbuch"
        >
          <HelpCircle size={20} />
        </button>
        <button
          onClick={onOpenSettings}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          aria-label="Einstellungen"
          title="Garten-Einstellungen"
        >
          <Settings size={20} />
        </button>
        <UserMenu />
      </div>

    </header>
  );
};

/**
 * TabBar Component - 48px height
 * Two tabs: Garten, Pflanzen + contextual info (zoom, grid, object count)
 * + 2D|3D-Toggle (#599); die Canvas-Infos gelten nur für die 2D-Ansicht.
 */
const TabBar = ({ activeTab, onTabChange, canvas, gridSize, viewMode, onViewModeChange }) => {
  const [zoomPercent, setZoomPercent] = useState(100);
  const [objectCount, setObjectCount] = useState(0);

  useEffect(() => {
    if (!canvas) return;

    const updateZoom = () => {
      const newZoom = Math.round(canvas.getZoom() * 100);
      setZoomPercent((prev) => (prev !== newZoom ? newZoom : prev));
    };

    // Only recurse into user-created groups (Ctrl+G). Compound objects like
    // PlantCircle are fabric groups too (objectType === 'plant') but conceptually
    // a single user object — counting their inner shapes inflates the counter (#429).
    const countRecursive = (obj) => {
      if (isUserGroup(obj) && obj.getObjects) {
        return obj.getObjects().reduce((sum, child) => sum + countRecursive(child), 0);
      }
      return 1;
    };

    const updateObjectCount = () => {
      const objects = canvas.getObjects();
      // Exclude non-user shapes: wizard background/outline/height-zones, grid,
      // placement ghosts, snap guides (transient during drag), vertex handles,
      // and the drawing tools' in-progress markers/previews (isTemporary).
      // Without isSnapGuide the count jumps +2 while dragging a plant (#429).
      const userObjects = objects.filter(
        (obj) =>
          obj.data &&
          !obj.data.locked &&
          !obj.data.isGhost &&
          !obj.data.isSnapGuide &&
          !obj.data.isGridLine &&
          !obj.data.isTemporary &&
          obj.data.type !== 'grid' &&
          obj.data.type !== 'vertex-handle'
      );
      const total = userObjects.reduce((sum, obj) => sum + countRecursive(obj), 0);
      // Functional bail-out keeps React from queueing a no-op update when the
      // count is unchanged (defense in depth against placement render loop, #429).
      setObjectCount((prev) => (prev !== total ? total : prev));
    };

    // Initial values
    updateZoom();
    updateObjectCount();

    // Listen for changes — after:render covers zoom, pan, and object changes
    canvas.on('after:render', updateZoom);
    canvas.on('object:added', updateObjectCount);
    canvas.on('object:removed', updateObjectCount);

    return () => {
      canvas.off('after:render', updateZoom);
      canvas.off('object:added', updateObjectCount);
      canvas.off('object:removed', updateObjectCount);
    };
  }, [canvas]);

  const tabs = [
    { id: 'garten', label: 'Garten', icon: Home },
    { id: 'pflanzen', label: 'Pflanzen', icon: Leaf },
  ];

  return (
    <div className="flex items-center justify-between h-[48px] bg-gray-50 border-b border-gray-200">
      <div className="flex">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              data-tour-id={tab.id === 'pflanzen' ? 'tour-plants' : undefined}
              className={`
                flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-colors
                ${
                  activeTab === tab.id
                    ? 'bg-white text-emerald-600 border-b-2 border-emerald-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              <Icon size={20} />
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-4 pr-4 text-xs text-gray-500">
        {viewMode === '2d' && (
          <>
            <span>Zoom: {zoomPercent}%</span>
            <span>Raster: {gridSize} cm</span>
            <span>{objectCount} {objectCount === 1 ? 'Objekt' : 'Objekte'}</span>
          </>
        )}
        <span data-tour-id="tour-viewmode" className="inline-flex">
          <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} />
        </span>
      </div>
    </div>
  );
};

/**
 * GroupPropertiesPanel - Properties display for a selected user-created group.
 * Shows position, size, rotation and an ungroup button.
 */
const GroupPropertiesPanel = ({ group, canvas, commandStack }) => {
  const subCount = group?.getObjects?.()?.length ?? 0;
  const left = Math.round(group?.left ?? 0);
  const top = Math.round(group?.top ?? 0);
  const width = Math.round((group?.width ?? 0) * (group?.scaleX ?? 1));
  const height = Math.round((group?.height ?? 0) * (group?.scaleY ?? 1));
  const angle = Math.round(group?.angle ?? 0);

  const handleUngroup = () => {
    if (!canvas || !group) return;
    const cmd = new UngroupObjectsCommand(canvas, group);
    commandStack.execute(cmd);
  };

  return (
    <div className="p-4">
      <div className="mb-4 pb-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">Gruppe ({subCount} Objekte)</h2>
        <p className="text-xs text-gray-500 mt-1">Doppelklick zum Bearbeiten</p>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">X</p>
            <p className="text-sm text-gray-700">{left} px</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Y</p>
            <p className="text-sm text-gray-700">{top} px</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">Breite</p>
            <p className="text-sm text-gray-700">{width} px</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Höhe</p>
            <p className="text-sm text-gray-700">{height} px</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Rotation</p>
          <p className="text-sm text-gray-700">{angle}°</p>
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={handleUngroup}
          className="w-full px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors"
          title="Strg+Shift+G"
        >
          Gruppierung lösen
        </button>
      </div>
    </div>
  );
};

/**
 * PropertiesPanel Component - 300px width
 * Displays properties for selected objects
 * Shows two-tab structure (Eigenschaften/Favoriten) when in Pflanzen tab
 */
const PropertiesPanel = ({ activeTab, activeTool, selectedObject, onUpdate, onDelete, onDeselect, gridSize, snapEnabled, onOpenCatalog, canvas, viewPanel }) => {
  const { commandStack } = useCommand();
  const [plantTab, setPlantTab] = React.useState('eigenschaften');
  const [gardenTab, setGardenTab] = React.useState('eigenschaften');

  // Check if it's a multi-selection (Fabric.js ActiveSelection)
  const isMultiSelection = selectedObject && selectedObject.type === 'activeselection';

  // Check if selected object is a user-created group
  const isGroup = selectedObject && isUserGroup(selectedObject);

  // Check if selected object is a rectangle, ellipse, line, polygon, or bezier
  const isRectangle = selectedObject && selectedObject.data?.type === 'rectangle';
  const isEllipse = selectedObject && selectedObject.data?.type === 'ellipse';
  const isLine = selectedObject && selectedObject.data?.type === 'line';
  const isPolygon = selectedObject && selectedObject.type === 'polygon';
  const isBezier = selectedObject && selectedObject.data?.type === 'bezier';

  // Check if object has garden object type metadata
  const hasGardenObjectType = selectedObject?.data?.gardenObjectType || selectedObject?.data?.objectTypeId;

  // Openings (#611 symbol gates + #690 windows) use the dedicated GateProperties
  // panel; legacy standalone gates fall through to the generic dynamic panel.
  const isGateSymbol = selectedObject && usesOpeningSymbol(selectedObject);

  // Check if selected object is a PlantCircle (for plant tab)
  const isPlant = selectedObject && selectedObject.objectType === 'plant';
  const selectedPlant = isPlant ? selectedObject.plantProperties : null;

  // Keyboard navigation for plant tab switching (Arrow Left/Right)
  React.useEffect(() => {
    if (activeTab !== 'pflanzen') return;

    const handleKeyDown = (e) => {
      // Check if user is typing in an input field
      const isTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) ||
                       document.activeElement?.contentEditable === 'true';
      if (isTyping) return;

      if (e.key === 'ArrowLeft') {
        // Cycle backwards: Eigenschaften <- Favoriten <- Ebenen <- Eigenschaften
        if (plantTab === 'favoriten') {
          setPlantTab('eigenschaften');
        } else if (plantTab === 'ebenen') {
          setPlantTab('favoriten');
        } else {
          setPlantTab('ebenen');
        }
      } else if (e.key === 'ArrowRight') {
        // Cycle forwards: Eigenschaften -> Favoriten -> Ebenen -> Eigenschaften
        if (plantTab === 'eigenschaften') {
          setPlantTab('favoriten');
        } else if (plantTab === 'favoriten') {
          setPlantTab('ebenen');
        } else {
          setPlantTab('eigenschaften');
        }
        // Auto-deselect plant when switching away from Eigenschaften tab via keyboard
        if (plantTab === 'eigenschaften' && isPlant && onDeselect) {
          onDeselect();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, plantTab, isPlant, onDeselect]);

  // View-Panel-Slot (#633): Blüte / Sonne & Schatten verdrängen den
  // Tab-Inhalt, solange sie offen sind (Tab-Zustände bleiben erhalten;
  // „Auswahl gewinnt" schließt das Panel in MainLayoutContent). Nach allen
  // Hooks platziert (Rules of Hooks).
  if (viewPanel) {
    return (
      <aside
        className="w-[300px] bg-gray-50 border-l border-gray-200 flex flex-col h-full"
        data-testid="view-panel-slot"
      >
        {/* p-3 statt p-4: lässt den 6×2-Blühkalender-Zellen ≥ 44 px Breite */}
        <div className="flex-1 overflow-y-auto bg-white p-3">{viewPanel}</div>
      </aside>
    );
  }

  // Special rendering for Pflanzen tab
  if (activeTab === 'pflanzen') {

    return (
      <aside className="w-[300px] bg-gray-50 border-l border-gray-200 flex flex-col h-full">
        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 bg-white" role="tablist">
          <button
            role="tab"
            aria-selected={plantTab === 'eigenschaften'}
            aria-controls="pflanzen-eigenschaften-panel"
            onClick={() => setPlantTab('eigenschaften')}
            className={`flex-1 h-12 px-4 text-sm font-medium transition-colors duration-200 border-b-2 flex items-center justify-center ${
              plantTab === 'eigenschaften'
                ? 'border-emerald-500 text-emerald-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Eigenschaften
          </button>
          <button
            role="tab"
            aria-selected={plantTab === 'favoriten'}
            aria-controls="pflanzen-favoriten-panel"
            onClick={() => {
              setPlantTab('favoriten');
              // Auto-deselect plant when switching to Favoriten tab
              if (isPlant && onDeselect) {
                onDeselect();
              }
            }}
            className={`flex-1 h-12 px-4 text-sm font-medium transition-colors duration-200 border-b-2 flex items-center justify-center ${
              plantTab === 'favoriten'
                ? 'border-emerald-500 text-emerald-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Favoriten
          </button>
          <button
            role="tab"
            aria-selected={plantTab === 'ebenen'}
            aria-controls="pflanzen-ebenen-panel"
            onClick={() => {
              setPlantTab('ebenen');
              // Auto-deselect plant when switching to Ebenen tab
              if (isPlant && onDeselect) {
                onDeselect();
              }
            }}
            className={`flex-1 h-12 px-4 text-sm font-medium transition-colors duration-200 border-b-2 flex items-center justify-center ${
              plantTab === 'ebenen'
                ? 'border-emerald-500 text-emerald-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Ebenen
          </button>
        </div>

        {/* Tab Content */}
        <div
          role="tabpanel"
          id={`pflanzen-${plantTab}-panel`}
          aria-labelledby={`pflanzen-${plantTab}-tab`}
          className="flex-1 overflow-y-auto"
        >
          {plantTab === 'eigenschaften' ? (
            <PlantPropertiesTab
              selectedPlant={selectedPlant}
              selectedCanvasObject={isPlant ? selectedObject : null}
              onDelete={onDelete}
            />
          ) : plantTab === 'favoriten' ? (
            <PlantFavoritesTab onOpenCatalog={onOpenCatalog} />
          ) : (
            <LayerPanel canvas={canvas} selectedObject={selectedObject} />
          )}
        </div>
      </aside>
    );
  }

  const currentTab = gardenTab;
  const setCurrentTab = setGardenTab;

  // Rendering for Garten tab
  return (
    <aside className="w-[300px] bg-gray-50 border-l border-gray-200 flex flex-col h-full">
      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200 bg-white" role="tablist">
        <button
          role="tab"
          aria-selected={currentTab === 'eigenschaften'}
          aria-controls={`${activeTab}-eigenschaften-panel`}
          onClick={() => setCurrentTab('eigenschaften')}
          className={`flex-1 h-12 px-4 text-sm font-medium transition-colors duration-200 border-b-2 flex items-center justify-center ${
            currentTab === 'eigenschaften'
              ? 'border-emerald-500 text-emerald-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Eigenschaften
        </button>
        <button
          role="tab"
          aria-selected={currentTab === 'ebenen'}
          aria-controls={`${activeTab}-ebenen-panel`}
          onClick={() => setCurrentTab('ebenen')}
          className={`flex-1 h-12 px-4 text-sm font-medium transition-colors duration-200 border-b-2 flex items-center justify-center ${
            currentTab === 'ebenen'
              ? 'border-emerald-500 text-emerald-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Ebenen
        </button>
      </div>

      {/* Tab Content */}
      <div
        role="tabpanel"
        id={`${activeTab}-${currentTab}-panel`}
        aria-labelledby={`${activeTab}-${currentTab}-tab`}
        className="flex-1 overflow-y-auto"
      >
        {currentTab === 'eigenschaften' ? (
          <>
            {/* Panel Header - Only show for shape-only objects (not garden objects or groups) */}
            {/* DynamicGardenObjectProperties and GroupProperties have their own headers */}
            {!hasGardenObjectType && !isGroup && (
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Eigenschaften</h2>
                <p className="text-xs text-gray-600 mt-1">
                  {isMultiSelection
                    ? 'Mehrere Objekte'
                    : isRectangle
                    ? 'Rechteck'
                    : isEllipse
                    ? 'Kreis/Ellipse'
                    : isLine
                    ? 'Linie'
                    : isPolygon
                    ? 'Polygon'
                    : isBezier
                    ? 'Kurve'
                    : activeTool === 'select'
                    ? 'Kein Element ausgewählt'
                    : `Werkzeug: ${getToolLabel(activeTool)}`}
                </p>
              </div>
            )}

            {/* Panel Content */}
            {isMultiSelection ? (
              <MultiSelectionProperties
                selection={selectedObject}
                onUpdate={onUpdate}
                onDelete={onDelete}
                pixelsPerCm={1}
                gridSize={gridSize}
                snapEnabled={snapEnabled}
              />
            ) : isGroup ? (
              <GroupPropertiesPanel
                group={selectedObject}
                canvas={canvas}
                commandStack={commandStack}
              />
            ) : isGateSymbol ? (
              <GateProperties
                gate={selectedObject}
                canvas={canvas}
                commandStack={commandStack}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ) : selectedObject ? (
              <DynamicGardenObjectProperties
                selectedObject={selectedObject}
                onUpdate={onUpdate}
                onDelete={onDelete}
                commandStack={commandStack}
                pixelsPerCm={1}
                onOpenCatalog={onOpenCatalog}
              />
            ) : (
              <div className="p-4">
                <div className="text-sm text-gray-500 text-center py-8">
                  Wählen Sie ein Element aus,
                  <br />
                  um dessen Eigenschaften anzuzeigen.
                </div>
              </div>
            )}
          </>
        ) : (
          <LayerPanel canvas={canvas} selectedObject={selectedObject} />
        )}
      </div>
    </aside>
  );
};

/**
 * Helper function to get tool label in German
 */
const getToolLabel = (toolId) => {
  const labels = {
    select: 'Auswählen',
    path: 'Weg',
    'lawn-edge': 'Rasenkante',
    'raised-bed': 'Hochbeet',
    fence: 'Zaun',
    gate: 'Gartentor',
    building: 'Gebäude',
    greenhouse: 'Gewächshaus',
    pond: 'Teich',
    terrace: 'Terrasse',
    compost: 'Kompost',
    'text-label': 'Text-Label',
    'plant-catalog': 'Pflanzen-Katalog',
  };
  return labels[toolId] || toolId;
};

/**
 * MobileEditorSubHeader — 44px bar shown in mobile editor view.
 * Contains: back button, garden name + "Ansicht", READ-ONLY badge, 2D|3D-Toggle (#599).
 */
const MobileEditorSubHeader = ({ onBack, viewMode, onViewModeChange }) => {
  const { settings } = useGardenSettings();
  const gardenName = settings?.name || 'Mein Garten';

  return (
    <div className="flex items-center h-[44px] px-3 bg-white border-b border-gray-200 flex-shrink-0 gap-2">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-emerald-600 font-medium flex-none"
        aria-label="Zurück zur Pflanzenliste"
      >
        <ChevronLeft size={18} />
        Zurück
      </button>
      <span className="flex-1 text-center text-sm font-medium text-gray-900 truncate">
        {gardenName} — Ansicht
      </span>
      {viewMode === '2d' && (
        <span className="flex-none text-[11px] font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
          READ-ONLY
        </span>
      )}
      <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} className="flex-none" />
    </div>
  );
};

export default MainLayout;
